import { IconGift } from '@tabler/icons-react'

import { Badge } from '@/components/sui/badge'
import { Button } from '@/components/sui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { cn } from '@/lib/utils'

import locales from './locales'

interface FreePlanBannerProps {
  depleted: boolean
  envelopesLeft: number
  envelopesTotal: number
  collapsed?: boolean
  onCtaClick: () => void
}

export function FreePlanBanner({
  depleted,
  envelopesLeft,
  envelopesTotal,
  collapsed = false,
  onCtaClick,
}: FreePlanBannerProps) {
  const { t } = useComponentTranslation(locales)
  const tooltip = `${t('title')} — ${
    depleted
      ? t('envelopesEmpty')
      : t('envelopesCount', { count: envelopesLeft })
  }`

  if (collapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                size="icon"
                aria-label={tooltip}
                className={cn(
                  'h-12 w-full font-semibold text-white',
                  depleted
                    ? 'bg-gradient-to-b from-red-500 to-red-800 hover:from-red-600 hover:to-red-900 focus-visible:border-red-500 focus-visible:ring-red-500/50'
                    : 'bg-gradient-to-b from-blue-500 to-blue-800 hover:from-blue-600 hover:to-blue-900 focus-visible:border-blue-500 focus-visible:ring-blue-500/50',
                )}
                onClick={onCtaClick}
              />
            }
          >
            <div className="flex flex-col items-center justify-center gap-0.5 leading-none">
              <IconGift className="size-3.5" />
              <span className="text-[11px] font-bold">{envelopesLeft}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // Bar shows consumption: empty on a fresh month, full once every envelope is used
  const usedPct =
    envelopesTotal > 0
      ? ((envelopesTotal - envelopesLeft) / envelopesTotal) * 100
      : 0

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg p-3 text-white',
        depleted
          ? 'bg-gradient-to-b from-red-500 to-red-800'
          : 'bg-gradient-to-b from-blue-500 to-blue-800',
      )}
    >
      <div className="flex items-center gap-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-white/20">
          <IconGift className="size-4" />
        </div>
        <span className="text-sm font-semibold">{t('title')}</span>
        <Badge variant="inverse" size="sm" className="ml-auto">
          FREE
        </Badge>
      </div>

      <p className="text-sm leading-snug text-white/70">
        {depleted ? t('descriptionDepleted') : t('description')}
      </p>

      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-semibold">
          {envelopesLeft === 0
            ? t('envelopesEmpty')
            : t('envelopesCount', { count: envelopesLeft })}
        </p>
        <ProgressBar pct={depleted ? 100 : usedPct} />
      </div>

      <Button
        size="sm"
        onClick={onCtaClick}
        className={cn(
          'mt-1 w-full bg-white hover:bg-white/90 focus-visible:border-white focus-visible:ring-white/50',
          depleted ? 'text-red-700' : 'text-blue-700',
        )}
      >
        {t('cta')}
      </Button>
    </div>
  )
}

function ProgressBar({ pct }: { pct: number }) {
  const clampedPct = Math.min(100, Math.max(0, pct))

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clampedPct)}
      className="relative h-1.5 overflow-hidden rounded-full bg-white/25"
    >
      {/* Stripes live on the consumed part only, so the motion belongs to what
          was actually used instead of glinting across the untouched remainder. */}
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        // No solid background behind the stripes: the translucent bands are
        // what let the card gradient through, so the pattern works on both the
        // blue and the depleted-red banner without knowing which one it is.
        style={{
          width: `${clampedPct}%`,
          backgroundImage:
            'repeating-linear-gradient(45deg, #fff 0 5px, rgb(255 255 255 / 0.45) 5px 10px)',
          backgroundSize: '14.14px 14.14px',
          animation: 'free-bar-stripes 1.1s linear infinite',
        }}
      />
    </div>
  )
}
